import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import { MultiSeriesChart } from '@/components/MultiSeriesChart';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';
import { formatLatency } from '@/utils/time';
import { WebhookDeliveryStats, WebhookDeliveryStatus } from '@/types/alert';
import {
    formatRelativeTimeI18n,
    formatChartDateMinutely,
    secondsToMilliseconds,
} from '@/utils/date';

const SPARKLINE_HEIGHT = 92;
const COLOR_SUCCESS = '#5AC4B4';
const COLOR_ERROR = '#cc3f3f';
const COLOR_NO_RESPONSE = '#9c9c9c';
const SPARKLINE_MARGIN = { top: 6, right: 4, left: -14, bottom: 0 };

interface Segment {
    status: WebhookDeliveryStatus;
    count: number;
    p95: number | null | undefined;
    labelKey: string;
    textClass: string;
    color: string;
    countKey: string;
    latencyKey: string;
}

interface WebhookDeliverySummaryProps {
    stats: WebhookDeliveryStats;
    selectedStatuses: WebhookDeliveryStatus[];
    onStatusToggle: (status: WebhookDeliveryStatus) => void;
    filters: ReactNode;
}

export function WebhookDeliverySummary({
    stats,
    selectedStatuses,
    onStatusToggle,
    filters,
}: WebhookDeliverySummaryProps) {
    const { t } = useTranslation(I18nNamespace.ALERT_RULES);

    const segments: Segment[] = [
        {
            status: WebhookDeliveryStatus.SUCCEEDED,
            count: stats.succeeded,
            p95: stats.p95SucceededMs,
            labelKey: 'webhookDeliveries.statusSuccess',
            textClass: 'text-green-bright',
            color: COLOR_SUCCESS,
            countKey: 'succeeded',
            latencyKey: 'p95Succeeded',
        },
        {
            status: WebhookDeliveryStatus.ERRORED,
            count: stats.errored,
            p95: stats.p95ErroredMs,
            labelKey: 'webhookDeliveries.statusError',
            textClass: 'text-danger-200',
            color: COLOR_ERROR,
            countKey: 'errored',
            latencyKey: 'p95Errored',
        },
        {
            status: WebhookDeliveryStatus.NO_RESPONSE,
            count: stats.noResponse,
            p95: stats.p95NoResponseMs,
            labelKey: 'webhookDeliveries.statusNoResponse',
            textClass: 'text-grey-200',
            color: COLOR_NO_RESPONSE,
            countKey: 'noResponse',
            latencyKey: 'p95NoResponse',
        },
    ];

    const isDimmed = (status: WebhookDeliveryStatus) =>
        selectedStatuses.length > 0 && !selectedStatuses.includes(status);

    const deliverySeries = segments.map(
        ({ status, countKey, labelKey, color }) => ({
            dataKey: countKey,
            label: t(labelKey),
            color,
            dimmed: isDimmed(status),
        }),
    );

    const latencySeries = segments.map(
        ({ status, latencyKey, labelKey, color }) => ({
            dataKey: latencyKey,
            label: t(labelKey),
            color,
            dimmed: isDimmed(status),
        }),
    );

    const failed = stats.errored + stats.noResponse;
    const failing = failed > stats.succeeded;
    const isEmpty = stats.total === 0;
    const rate = isEmpty
        ? 0
        : Math.round(
              ((failing ? failed : stats.succeeded) / stats.total) * 100,
          );

    return (
        <div className="mb-4 rounded-lg border border-grey-600 bg-grey-700/40 p-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                {isEmpty ? (
                    <span className="text-sm text-grey-200">
                        {t('webhookDeliveries.summaryEmpty')}
                    </span>
                ) : (
                    <>
                        <span
                            className={cn(
                                'text-lg',
                                failing
                                    ? 'text-danger-200'
                                    : 'text-green-bright',
                            )}
                        >
                            {rate}%
                        </span>
                        <span className="text-sm text-grey-200">
                            {t(
                                failing
                                    ? 'webhookDeliveries.summaryFailed'
                                    : 'webhookDeliveries.summarySucceeded',
                            )}
                        </span>
                    </>
                )}
                <div className="ml-auto">{filters}</div>
            </div>

            {isEmpty ? null : (
                <>
                    <div className="mt-1 text-xs text-grey-300">
                        {t('webhookDeliveries.summaryTotal', {
                            count: stats.total,
                        })}
                        {stats.p95LatencyMs != null &&
                            ` · ${t('webhookDeliveries.summaryP95', { latency: formatLatency(stats.p95LatencyMs) })}`}
                        {` · ${
                            stats.lastSuccessAt
                                ? t('webhookDeliveries.summaryLastSuccess', {
                                      time: formatRelativeTimeI18n(
                                          secondsToMilliseconds(
                                              stats.lastSuccessAt,
                                          ),
                                      ),
                                  })
                                : t('webhookDeliveries.summaryNeverSucceeded')
                        }`}
                    </div>

                    {stats.chartData.length > 1 && (
                        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <div className="mb-1 text-xs text-grey-300">
                                    {t('webhookDeliveries.chartDeliveries')}
                                </div>
                                <MultiSeriesChart
                                    data={stats.chartData}
                                    series={deliverySeries}
                                    height={SPARKLINE_HEIGHT}
                                    showGrid={false}
                                    margin={SPARKLINE_MARGIN}
                                    xAxisTickFormatter={formatChartDateMinutely}
                                    xAxisInterval="preserveStartEnd"
                                />
                            </div>
                            <div>
                                <div className="mb-1 text-xs text-grey-300">
                                    {t('webhookDeliveries.chartLatency')}
                                </div>
                                <MultiSeriesChart
                                    data={stats.chartData}
                                    series={latencySeries}
                                    height={SPARKLINE_HEIGHT}
                                    showGrid={false}
                                    margin={SPARKLINE_MARGIN}
                                    valueFormatter={formatLatency}
                                    yAxisTickFormatter={formatLatency}
                                    xAxisTickFormatter={formatChartDateMinutely}
                                    xAxisInterval="preserveStartEnd"
                                />
                            </div>
                        </div>
                    )}

                    <div className="mt-3 flex flex-wrap gap-2">
                        {segments.map(
                            ({ status, count, p95, labelKey, textClass }) => (
                                <button
                                    key={status}
                                    type="button"
                                    onClick={() => onStatusToggle(status)}
                                    className={cn(
                                        'flex items-baseline gap-1.5 rounded-[5px] border px-2 py-1 text-xs transition-colors',
                                        selectedStatuses.includes(status)
                                            ? 'border-grey-400 bg-grey-600'
                                            : 'border-transparent bg-grey-700 hover:border-grey-500',
                                    )}
                                >
                                    <span className={textClass}>
                                        <AnimatedNumber value={count} />
                                    </span>
                                    <span className="text-grey-200">
                                        {t(labelKey)}
                                    </span>
                                    {p95 != null && (
                                        <span className="text-grey-400">
                                            {formatLatency(p95)}
                                        </span>
                                    )}
                                </button>
                            ),
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
